import BaseController from './base_controller'

export default class extends BaseController {
  static targets = ['pair']
  static values = {
    rangeSpeedMessage: String,
    rangeConsumptionMessage: String,
    differenceEcoMessage: String,
    differenceFullMessage: String,
    orderMessage: String
  }

  connect() {
    this.errors = new WeakMap()
    this.boundChange = this.handleChange.bind(this)
    this.element.addEventListener('change', this.boundChange)
  }

  disconnect() {
    this.element.removeEventListener('change', this.boundChange)
    this.inputs().forEach(input => this.disposeTooltip(input))
  }

  inputs() {
    return Array.from(this.element.querySelectorAll('input[data-speed], input[data-consumption]'))
  }

  handleChange(event) {
    const input = event.target
    if (!(input instanceof HTMLInputElement)) return
    const isSpeed = 'speed' in input.dataset
    const isConsumption = 'consumption' in input.dataset
    if (!isSpeed && !isConsumption) return

    const affected = new Set([input])
    if (isSpeed) {
      this.computeDifference(input, affected)
      this.computeRange(input)
      this.computeOrder(input, input.dataset.speed, 'speed', affected)
    } else {
      this.computeRange(input)
      this.computeOrder(input, input.dataset.consumption, 'consumption', affected)
    }
    affected.forEach(el => this.refresh(el))
  }

  // value outside [min, max]. Absent min/max (TBN speeds, MGO rows) → NaN → no error.
  computeRange(input) {
    const max = parseFloat(input.dataset.max)
    const min = parseFloat(input.dataset.min)
    const value = parseFloat(input.value)
    this.setError(input, 'range', value > max || value < min)
  }

  // full and eco speeds must differ by at least 0.5kn. A blank side (NaN) does not
  // flag, so a single-column entry is tolerated.
  computeDifference(input, affected) {
    const opposite = this.opposite(input, 'speed')
    const tooClose = Math.abs(parseFloat(this.valueOf(opposite)) - parseFloat(input.value)) < 0.5
    this.setError(input, 'difference', tooClose)
    if (opposite) {
      this.setError(opposite, 'difference', tooClose)
      affected.add(opposite)
    }
  }

  // the higher (full) value must be entered left of the lower (eco) one.
  computeOrder(input, column, label, affected) {
    const opposite = this.opposite(input, label)
    const oppositeValue = parseFloat(this.valueOf(opposite))
    const value = parseFloat(input.value)
    const outOfOrder = (column === 'full' && oppositeValue > value) ||
      (column === 'eco' && oppositeValue < value)
    this.setError(input, 'order', outOfOrder)
    if (opposite) {
      this.setError(opposite, 'order', outOfOrder)
      affected.add(opposite)
    }
  }

  opposite(input, label) {
    const other = input.dataset[label] === 'full' ? 'eco' : 'full'
    const pair = this.pairTargets.find(cell => cell.contains(input))
    return pair ? pair.querySelector(`input[data-${label}="${other}"]`) : null
  }

  valueOf(input) {
    return input ? input.value : ''
  }

  errorsFor(input) {
    let flags = this.errors.get(input)
    if (!flags) {
      flags = {}
      this.errors.set(input, flags)
    }
    return flags
  }

  setError(input, kind, present) {
    const flags = this.errorsFor(input)
    if (present) flags[kind] = true
    else delete flags[kind]
  }

  refresh(input) {
    const flags = this.errorsFor(input)
    const hasError = !!(flags.range || flags.difference || flags.order)
    input.classList.toggle('field-error', hasError)
    const message = hasError ? this.messageFor(input, flags) : ''
    if (message) {
      addFormErrorTooltip($(input), message)
    } else {
      this.disposeTooltip(input)
    }
  }

  // Precedence matches the legacy tooltip: range → difference → order.
  messageFor(input, flags) {
    if (flags.range) {
      const template = 'speed' in input.dataset ? this.rangeSpeedMessageValue : this.rangeConsumptionMessageValue
      return template
        .replace('{max}', parseFloat(input.dataset.max).toFixed(2))
        .replace('{min}', parseFloat(input.dataset.min).toFixed(2))
    }
    if (flags.difference) {
      return input.dataset.speed === 'eco' ? this.differenceEcoMessageValue : this.differenceFullMessageValue
    }
    if (flags.order) return this.orderMessageValue
    return ''
  }

  disposeTooltip(input) {
    const tooltip = bootstrap.Tooltip.getInstance(input)
    if (tooltip) tooltip.dispose()
  }
}
